// src/AppWrapper.js
import React, { useEffect, useMemo, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { onAuthStateChanged, signOut, getIdTokenResult, signInWithCustomToken } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, app, ensureDurableAuthPersistence } from "./firebase";

import ClockIn from "./pages/ClockIn";
import ClockOut from "./pages/ClockOut";
import StaffDashboard from "./pages/StaffDashboard";
import StaffPortalPage from "./pages/StaffPortalPage";
import ManagerLogin from "./pages/ManagerLogin";
import ManagerDashboard from "./pages/ManagerDashboard";
import ReportsPage from "./pages/ReportsPage";
import SettingsPage from "./pages/SettingsPage";
import DashContentBuilder from "./components/DashContentBuilder";
import FixSubheadings from "./pages/FixSubheadings";
import OrderSheet from "./pages/OrderSheet";
import DynamicSectionPage from "./pages/DynamicSectionPage";
import LinkDevice from "./pages/LinkDevice";
import DeviceKilledModal from "./components/DeviceKilledModal";
import useResolvedBusinessMode from "./hooks/useResolvedBusinessMode";
import SecurityPatrolsPage from "./pages/security/SecurityPatrolsPage";
import SecurityIncidentsPage from "./pages/security/SecurityIncidentsPage";
import SecurityUnlinkPage from "./pages/security/SecurityUnlinkPage";
import { isFieldOpsMode } from "./utils/businessMode";
import { readBinding, saveBinding, clearBinding, isBound } from "./utils/storeBinding";

import {
  RequireStoreLink,
  RequireStaffSession,
  RequireManagerSession,
  RequireNoPop,
} from "./guards";

function parseInstallParamsFromSearch(search) {
  const sp = new URLSearchParams(search || "");
  const first = (...keys) => keys.map((k) => sp.get(k)).find(Boolean) || "";
  return {
    orgId: first("org", "orgId", "ORG", "ORGID"),
    storeId: first("store", "storeId", "STORE", "STOREID"),
    token: first("token", "firstStore", "TOKEN", "FIRSTSTORE"),
    exp: Number(first("exp")) || 0,
    orgName: first("orgName"),
    storeName: first("storeName"),
    mode: first("mode", "businessMode"),
    appVariant: first("appVariant", "variant"),
  };
}

function isDeviceRelinkRequired() {
  try {
    return localStorage.getItem("neo.relinkRequired") === "1";
  } catch {
    return false;
  }
}

function persistInstallModeHints(obj) {
  try {
    const mode = String(obj?.mode || "").trim();
    const appVariant = String(obj?.appVariant || "").trim();

    if (!mode && !appVariant) return;

    localStorage.setItem("installParams", JSON.stringify(obj));

    if (mode) {
      localStorage.setItem("businessMode", mode);
      if (isFieldOpsMode(mode)) {
        localStorage.setItem("deviceType", "neo_security");
      }
    }

    if (appVariant) {
      localStorage.setItem("appVariant", appVariant);
    }
  } catch {
    // no-op
  }
}

function clearLinkedDeviceAndBounce(reason = "killed") {
  const keys = [
    "orgId",
    "storeId",
    "storeName",
    "orgName",
    "deviceUid",
    "deviceType",
    "businessMode",
    "appVariant",
    "managerId",
    "managerName",
    "staffName",
    "staffId",
    "managerSession",
    "cashup_manager_session",
    "session_manager",
    "cashup_staff_session",
    "staffSession",
    "session_staff",
    "cashup.session",
    "linkedOrgId",
    "linkedStoreId",
    "linkedStoreName",
    "workspacePinVerified",
    "deviceLinked",
    "deviceLinkToken",
  ];

  try {
    keys.forEach((k) => localStorage.removeItem(k));
    clearBinding();
    localStorage.setItem("neo.relinkRequired", "1");
  } catch {}

  try {
    sessionStorage.clear();
  } catch {}

  signOut(auth).catch(() => {});

  const suffix = reason ? `?${reason}=1` : "";
  window.location.replace(`/link${suffix}`);
}

function extractDeviceCustomToken(data = {}) {
  return (
    data?.customToken ||
    data?.authToken ||
    data?.deviceToken ||
    data?.result?.customToken ||
    data?.payload?.customToken ||
    data?.data?.customToken ||
    ""
  );
}

async function claimsMatchLinkedDevice(user, binding) {
  if (!user || !binding?.orgId || !binding?.storeId) return false;
  const tokenResult = await getIdTokenResult(user, true).catch(() => null);
  const claims = tokenResult?.claims || {};
  const claimOrgId = String(claims?.orgId || "");
  const claimStoreId = String(claims?.storeId || "");
  return (
    user.isAnonymous !== true &&
    claimOrgId === String(binding.orgId) &&
    (!claimStoreId || claimStoreId === String(binding.storeId))
  );
}

async function recoverLinkedDeviceAuth(binding) {
  if (!binding?.orgId || !binding?.storeId) return false;

  // First give Firebase persistence a chance to restore the durable custom-token
  // session. iOS installed PWAs can emit a transient anonymous/stale user while
  // IndexedDB auth state is settling after an update/relaunch.
  await ensureDurableAuthPersistence().catch(() => {});
  if (await claimsMatchLinkedDevice(auth.currentUser, binding)) return true;

  // A linked device is durable. Ask the existing device heartbeat endpoint to
  // validate that registration and, when the backend supplies a refreshed device
  // custom token, silently restore Firebase auth without making the store relink.
  try {
    const fns = getFunctions(app, "us-central1");
    const heartbeat = httpsCallable(fns, "heartbeatKioskDevice");
    // IMPORTANT: recovery must identify the durable linked kiosk, never the
    // transient anonymous Firebase user Safari may create after an update.
    const deviceUid = binding.deviceUid || localStorage.getItem("deviceUid") || "";
    let deviceSecret = String(binding.recoverySecret || "");
    if (!deviceSecret) {
      try {
        const installParams = JSON.parse(localStorage.getItem("installParams") || "{}");
        deviceSecret = String(installParams?.token || "");
      } catch {
        deviceSecret = "";
      }
    }

    // Legacy bridge for already-linked devices created before persistent
    // recovery credentials existed.
    if (!deviceSecret && String(deviceUid).startsWith("kiosk_")) {
      deviceSecret = String(deviceUid).slice("kiosk_".length);
    }

    if (!deviceSecret) {
      recoveryCredentialBlocked = true;
      try { localStorage.setItem("neo.deviceRecoveryRequired", "1"); } catch {}
      window.dispatchEvent(new CustomEvent("neo:device-auth-recovery", {
        detail: {
          stage: "legacy-relink-required",
          errorCode: "device/recovery-credential-missing",
          errorMessage: "Legacy device needs one one-time relink to seed persistent authentication recovery.",
        },
      }));
      return false;
    }

    // IPAD-08: keep the current anonymous session alive while requesting a
    // refreshed kiosk token. Anonymous Firebase Auth still satisfies older
    // signedIn-only Neo reads, so signing it out here caused Staff/Managers to
    // disappear before recovery completed. heartbeatKioskDevice validates the
    // durable device UID + install secret itself; request.auth.uid is not used
    // as the recovery identity.

    const res = await heartbeat({
      orgId: binding.orgId,
      storeId: binding.storeId,
      deviceUid,
      recoverAuth: true,
      deviceSecret,
      recoverySecret: deviceSecret,
      recoveryVersion: "BUILD-14",
    });
    const data = res?.data || {};
    console.log("[Neo BUILD-14] device auth recovery response", {
      status: data?.status || null,
      recovered: data?.authRecovered === true,
      hasCustomToken: Boolean(extractDeviceCustomToken(data)),
      deviceUid: data?.deviceUid || null,
    });

    if (
      data.shouldUnlink === true ||
      ["killed", "unlinked", "disabled", "revoked"].includes(String(data.status || "").toLowerCase())
    ) {
      return false;
    }

    const customToken = extractDeviceCustomToken(data);
    if (customToken && String(customToken).length >= 20) {
      await signInWithCustomToken(auth, String(customToken));
      await auth.currentUser?.getIdToken(true);
      if (await claimsMatchLinkedDevice(auth.currentUser, binding)) {
        saveBinding({
          ...binding,
          deviceUid: data.deviceUid ? String(data.deviceUid) : (binding.deviceUid || auth.currentUser?.uid || ""),
          orgName: data.orgName ? String(data.orgName) : binding.orgName,
          storeName: data.storeName ? String(data.storeName) : binding.storeName,
          recoverySecret: String(data.recoverySecret || binding.recoverySecret || deviceSecret || ""),
        });
        recoveryCredentialBlocked = false;
        try { localStorage.removeItem("neo.deviceRecoveryRequired"); } catch {}
        return true;
      }
    }
  } catch (err) {
    const code = String(err?.code || "");
    const message = String(err?.message || "");
    if (
      code.includes("permission-denied") &&
      message.toLowerCase().includes("recovery credential")
    ) {
      recoveryCredentialBlocked = true;
      try { localStorage.setItem("neo.deviceRecoveryRequired", "1"); } catch {}
      window.dispatchEvent(new CustomEvent("neo:device-auth-recovery", {
        detail: {
          stage: "legacy-relink-required",
          errorCode: code || "functions/permission-denied",
          errorMessage: message || "Legacy device recovery credential is missing or invalid.",
        },
      }));
    }
    console.warn("[Neo BUILD-14] silent device auth recovery deferred", err);
  }

  return false;
}

let recoveryCredentialBlocked = false;

function DeviceBindingWatcher() {
  const [showKilledModal, setShowKilledModal] = useState(false);
  const [unlinkReason, setUnlinkReason] = useState("killed");

  const handleKilledConfirm = () => {
    clearLinkedDeviceAndBounce(unlinkReason || "killed");
  };

  useEffect(() => {
    let stopped = false;
    let intervalId = null;
    let recoveryInFlight = false;

    const runCheck = async () => {
      if (isDeviceRelinkRequired()) return;

      const binding = readBinding();
      const orgId = binding.orgId || "";
      const storeId = binding.storeId || "";
      if (!orgId || !storeId) return;

      // IPAD-05: never convert a missing/stale Firebase session into an unlink.
      // Recover silently; only an explicit backend revocation may unlink a kiosk.
      const authMatches = await claimsMatchLinkedDevice(auth.currentUser, binding);
      if (!authMatches && !recoveryInFlight && !recoveryCredentialBlocked) {
        recoveryInFlight = true;
        try {
          await recoverLinkedDeviceAuth(binding);
        } finally {
          recoveryInFlight = false;
        }
      }

      const deviceUid =
        binding.deviceUid || localStorage.getItem("deviceUid") || auth.currentUser?.uid || "";
      if (!deviceUid) return;

      try {
        const fns = getFunctions(app, "us-central1");
        const heartbeat = httpsCallable(fns, "heartbeatKioskDevice");
        const res = await heartbeat({ orgId, storeId, deviceUid });
        const data = res?.data || {};
        if (stopped) return;

        saveBinding({
          orgId,
          storeId,
          deviceUid: data.deviceUid ? String(data.deviceUid) : deviceUid,
          orgName: data.orgName ? String(data.orgName) : binding.orgName,
          storeName: data.storeName ? String(data.storeName) : binding.storeName,
          businessMode: data.businessMode ? String(data.businessMode) : binding.businessMode,
          appVariant: data.appVariant ? String(data.appVariant) : binding.appVariant,
          deviceType: data.businessMode && isFieldOpsMode(String(data.businessMode)) ? "neo_security" : binding.deviceType,
        });

        if (
          data.shouldUnlink === true ||
          ["killed", "unlinked", "disabled", "revoked"].includes(String(data.status || "").toLowerCase())
        ) {
          setUnlinkReason(String(data.status || "killed").toLowerCase());
          setShowKilledModal(true);
          if (intervalId) {
            window.clearInterval(intervalId);
            intervalId = null;
          }
        }
      } catch (err) {
        console.warn("heartbeatKioskDevice failed", err);
      }
    };

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (stopped) return;

      // iOS PWA Firebase Auth may restore the previous custom-token user
      // after local/session storage was cleared. Never let those stale claims
      // recreate a binding while the device is explicitly waiting to relink.
      if (isDeviceRelinkRequired()) {
        return;
      }

      try {
        const existing = readBinding();
        if (user && (!existing.orgId || !existing.storeId)) {
          const tokenResult = await getIdTokenResult(user, true).catch(() => null);
          const claims = tokenResult?.claims || {};
          if (claims?.orgId && claims?.storeId) {
            saveBinding({
              orgId: String(claims.orgId),
              storeId: String(claims.storeId),
              storeName: claims.storeName ? String(claims.storeName) : existing.storeName,
              orgName: claims.orgName ? String(claims.orgName) : existing.orgName,
              deviceUid: existing.deviceUid || user.uid,
              deviceType: existing.deviceType,
              businessMode: existing.businessMode,
              appVariant: existing.appVariant,
            });
          }
        } else if (user && !existing.deviceUid && user.isAnonymous !== true) {
          saveBinding({ ...existing, deviceUid: user.uid });
        }
      } catch {
        /* ignore */
      }

      runCheck();
      if (intervalId) window.clearInterval(intervalId);
      // C4 cost optimisation: the device heartbeat only needs to confirm the
      // binding/revocation state, not poll Cloud Functions every 5 seconds.
      // Keep the immediate check on auth restoration and then check once a
      // minute. A killed/unlinked kiosk is therefore detected within ~60s
      // while cutting heartbeat invocations by up to 12x.
      intervalId = window.setInterval(runCheck, 60000);
    });

    runCheck();

    return () => {
      stopped = true;
      if (intervalId) window.clearInterval(intervalId);
      unsub();
    };
  }, []);

  return <DeviceKilledModal open={showKilledModal} onConfirm={handleKilledConfirm} />;
}

function RetailRootEntry() {
  const loc = useLocation();
  const [ready, setReady] = useState(false);
  const [linked, setLinked] = useState(() => isBound());

  const installState = useMemo(() => {
    const obj = parseInstallParamsFromSearch(loc.search);
    const hasParams = !!(obj.orgId || obj.storeId || obj.token);

    if (hasParams) {
      persistInstallModeHints(obj);
    }

    let saved = null;
    try {
      saved = JSON.parse(localStorage.getItem("installParams") || "null");
    } catch {
      saved = null;
    }

    const hasSaved = !!(saved && (saved.orgId || saved.storeId || saved.token));

    return {
      obj,
      hasInstallParams: hasParams,
      hasSavedInstall: hasSaved,
    };
  }, [loc.search]);

  useEffect(() => {
    let cancelled = false;

    async function bootstrapBinding() {
      // A manager-authorised reset must win over EVERY persistence layer,
      // including iOS Firebase Auth/IndexedDB. Until a new PIN link succeeds,
      // the old device claims are not allowed to rebuild the binding.
      if (isDeviceRelinkRequired()) {
        try {
          clearBinding();
        } catch {}
        if (!cancelled) {
          setLinked(false);
          setReady(true);
        }
        return;
      }

      if (isBound()) {
        if (!cancelled) {
          setLinked(true);
          setReady(true);
        }
        return;
      }

      const user = auth.currentUser;
      if (user) {
        try {
          const tokenResult = await getIdTokenResult(user, true);
          const claims = tokenResult?.claims || {};
          if (claims?.orgId && claims?.storeId) {
            saveBinding({
              orgId: String(claims.orgId),
              storeId: String(claims.storeId),
              storeName: claims.storeName ? String(claims.storeName) : "",
              orgName: claims.orgName ? String(claims.orgName) : "",
              deviceUid: user.uid,
            });
            if (!cancelled) {
              setLinked(true);
              setReady(true);
            }
            return;
          }
        } catch {
          /* ignore */
        }
      }

      const unsub = onAuthStateChanged(auth, async (nextUser) => {
        try {
          if (nextUser && !isBound()) {
            const tokenResult = await getIdTokenResult(nextUser, true).catch(() => null);
            const claims = tokenResult?.claims || {};
            if (claims?.orgId && claims?.storeId) {
              saveBinding({
                orgId: String(claims.orgId),
                storeId: String(claims.storeId),
                storeName: claims.storeName ? String(claims.storeName) : "",
                orgName: claims.orgName ? String(claims.orgName) : "",
                deviceUid: nextUser.uid,
              });
            }
          }
        } finally {
          if (!cancelled) {
            setLinked(isBound());
            setReady(true);
          }
          unsub();
        }
      });
    }

    bootstrapBinding();

    return () => {
      cancelled = true;
    };
  }, [loc.key, loc.search]);

  if (isDeviceRelinkRequired()) {
    return <Navigate to="/link" replace />;
  }

  if (installState.hasInstallParams && !isBound()) return <Navigate to={`/link${loc.search}`} replace />;

  if (!ready) return <ModeLoadingScreen />;

  if (!linked && installState.hasSavedInstall) return <Navigate to="/link" replace />;

  return (
    <RequireStoreLink>
      <ClockIn />
    </RequireStoreLink>
  );
}

function ModeLoadingScreen() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background:
          "radial-gradient(circle at top, rgba(14,165,233,.16), transparent 34%), linear-gradient(180deg, #020617 0%, #0f172a 100%)",
        color: "#e2e8f0",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "min(520px, 100%)",
          borderRadius: 24,
          background: "rgba(15,23,42,.78)",
          border: "1px solid rgba(255,255,255,.08)",
          padding: 24,
          textAlign: "center",
          boxShadow: "0 22px 60px rgba(0,0,0,.35)",
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: ".08em", opacity: 0.8 }}>
          NEO MODE LOADER
        </div>
        <div style={{ marginTop: 12, fontSize: 26, fontWeight: 900 }}>Preparing your workspace</div>
        <div style={{ marginTop: 10, lineHeight: 1.6, opacity: 0.85 }}>
          Checking whether this organization should load the classic store device flow
          or the new field-services experience.
        </div>
      </div>
    </div>
  );
}

export default function AppWrapper() {
  const appLocation = useLocation();
  const { businessMode, loading } = useResolvedBusinessMode();

  const installHintMode = (() => {
    try {
      const current = parseInstallParamsFromSearch(window.location.search);
      if (current.mode) return current.mode;
      return localStorage.getItem("businessMode") || "";
    } catch {
      return "";
    }
  })();

  const fieldOpsMode = isFieldOpsMode(businessMode || installHintMode);
  const deviceType = readBinding().deviceType || localStorage.getItem("deviceType");
  const resolvedFieldOpsMode = fieldOpsMode || deviceType === "neo_security";

  useEffect(() => {
    const current = parseInstallParamsFromSearch(window.location.search);
    if (current.mode || current.appVariant) {
      persistInstallModeHints(current);
    }
  }, []);

  // Never block the device-link/reset page behind the mode loader. Mobile PWAs
  // can arrive here specifically to repair a broken binding, so the link screen
  // must render even when org mode lookup is slow or auth/storage is recovering.
  // A linked retail Neo device already has enough local binding context to render.
  // Keep resolving business mode in the background instead of replacing the live
  // workspace with "Preparing your workspace" on every remount/update cycle.
  // Only show the mode loader before a device has actually been linked.
  if (
    loading &&
    !resolvedFieldOpsMode &&
    appLocation.pathname !== "/link" &&
    !isBound()
  ) {
    return <ModeLoadingScreen />;
  }

  return (
    <>
      <DeviceBindingWatcher />

      <Routes>
        <Route
          path="/"
          element={<RetailRootEntry />}
        />

        <Route
          path="/clock-out"
          element={
            <RequireStoreLink>
              <ClockOut />
            </RequireStoreLink>
          }
        />

        <Route path="/link" element={<LinkDevice />} />
        <Route path="/select-store" element={<Navigate to="/link" replace />} />

        <Route
          path="/security"
          element={<Navigate to="/" replace />}
        />
        <Route
          path="/security/shift"
          element={<Navigate to="/" replace />}
        />
        <Route
          path="/security/patrols"
          element={resolvedFieldOpsMode ? <SecurityPatrolsPage /> : <Navigate to="/" replace />}
        />
        <Route
          path="/security/incidents"
          element={resolvedFieldOpsMode ? <SecurityIncidentsPage /> : <Navigate to="/" replace />}
        />
        <Route
          path="/security/unlink"
          element={resolvedFieldOpsMode ? <SecurityUnlinkPage /> : <Navigate to="/" replace />}
        />

        <Route
          path="/staff-dashboard"
          element={
            <RequireNoPop redirectTo="/">
              <RequireStoreLink>
                <RequireStaffSession>
                  <StaffDashboard />
                </RequireStaffSession>
              </RequireStoreLink>
            </RequireNoPop>
          }
        />

        <Route
          path="/staff-portal"
          element={
            <RequireNoPop redirectTo="/">
              <RequireStoreLink>
                <RequireStaffSession>
                  <StaffPortalPage />
                </RequireStaffSession>
              </RequireStoreLink>
            </RequireNoPop>
          }
        />

        <Route
          path="/cash-up"
          element={<Navigate to="/section/Cashup" replace />}
        />

        <Route
          path="/order-sheet"
          element={
            <RequireStoreLink>
              <RequireStaffSession>
                <OrderSheet />
              </RequireStaffSession>
            </RequireStoreLink>
          }
        />

        <Route
          path="/section/:sectionName"
          element={
            <RequireStoreLink>
              <RequireStaffSession>
                <DynamicSectionPage />
              </RequireStaffSession>
            </RequireStoreLink>
          }
        />

        <Route path="/manager-login" element={<ManagerLogin />} />

        <Route
          path="/manager-dashboard"
          element={
            <RequireNoPop redirectTo="/manager-login">
              <RequireStoreLink>
                <RequireManagerSession>
                  <ManagerDashboard />
                </RequireManagerSession>
              </RequireStoreLink>
            </RequireNoPop>
          }
        />

        <Route
          path="/manager-reports"
          element={
            <RequireNoPop redirectTo="/manager-login">
              <RequireStoreLink>
                <RequireManagerSession>
                  <ReportsPage />
                </RequireManagerSession>
              </RequireStoreLink>
            </RequireNoPop>
          }
        />

        <Route
          path="/manager-settings"
          element={
            <RequireNoPop redirectTo="/manager-login">
              <RequireStoreLink>
                <RequireManagerSession>
                  <SettingsPage />
                </RequireManagerSession>
              </RequireStoreLink>
            </RequireNoPop>
          }
        />

        <Route path="/admin-dashcontent" element={<DashContentBuilder />} />
        <Route path="/fix-subheadings" element={<FixSubheadings />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
